/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from 'react'

import { styled } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import MDBox from 'components/MDBox'
import { useAuth } from 'context/auth'
import { updateRoutes, useMaterialUIController } from 'context/materialUI'
import FeedbackPage from 'layouts/feedback/internal'
import ChangeLog from 'modules/ChangeLog'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import ManageCard from 'modules/ManageCard'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { ROUTE_PATHS, RouteType } from 'routes'

import MainContent from './components/MainContent'
import { FormContext, initLocalForm } from './constants'
import { BusinessAccountForm } from './types'

const PrintableBox = styled(MDBox)(({ theme }) => ({
	'@media print': {
		marginTop: theme.spacing(5),
		marginRight: theme.spacing(2),
		marginLeft: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
}))

function BusinessAccount(): JSX.Element {
	const contentRef = useRef<HTMLDivElement>(null)
	const reactToPrintFn = useReactToPrint({ contentRef })

	const queryClient = useQueryClient()

	const [controller, dispatch] = useMaterialUIController()
	const { user } = useAuth()
	const { routes } = controller
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const [localForm, setLocalForm] = useState<BusinessAccountForm>(initLocalForm)

	const navigate = useNavigate()
	const location = useLocation()
	const locationPathNameParts = location.pathname.split('/')
	const accountId = locationPathNameParts[locationPathNameParts.length - 1]
	const isNew = accountId === 'new'

	const fetchBusinessAccount = async () => {
		const response = await axios.get<BusinessAccountForm>(
			`${process.env.REACT_APP_API_URL}/business-account`,
			{
				params: {
					account_id: accountId,
					timeframe: localForm.analytics?.timeframe,
					feedbackType: localForm.analytics?.feedbackType,
				},
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const saveNewBusinessAccount = async () => {
		const response = await axios.post<{ id: string }>(
			`${process.env.REACT_APP_API_URL}/business-account`,
			{ ...localForm },
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const updateBusinessAccount = async () => {
		const response = await axios.put<BusinessAccountForm>(
			`${process.env.REACT_APP_API_URL}/business-account`,
			{ ...localForm },
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const deleteBusinessAccount = async () => {
		await axios.delete(`${process.env.REACT_APP_API_URL}/business-account`, {
			params: {
				account_id: localForm.details.id,
			},
			headers: {
				Authorization: await user.auth.getIdToken(),
			},
		})
	}

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: [`business-account-${accountId}`],
		queryFn: fetchBusinessAccount,
		enabled: !isNew,
	})

	useEffect(() => {
		if (!isNew && data) {
			const feedbackRoutes = data.feedbackTable.table.rows.map(feedback => {
				const route: RouteType = {
					type: 'invisible',
					name: `Feedback: ${feedback.id}`,
					key: `feedback-${feedback.id}`,
					route: `${ROUTE_PATHS.FEEDBACK.BASE}/${feedback.id}`,
					component: <FeedbackPage />,
					authRequired: true,
				}
				return route
			})
			const newRoutes = feedbackRoutes.filter(
				newRoute => !routes.some(existingRoute => existingRoute.key === newRoute.key)
			)

			if (newRoutes.length > 0) {
				updateRoutes(dispatch, routes, newRoutes)
			}
			setLocalForm(data)
		} else {
			setLocalForm(initLocalForm)
		}
	}, [data, isNew, dispatch, routes])

	useEffect(() => {
		if (!isNew) {
			refetch()
		}
	}, [localForm.analytics?.timeframe, localForm.analytics?.feedbackType, isNew, refetch])

	return (
		<FormContext.Provider value={{ formEdited, setFormEdited, isNew, localForm, setLocalForm }}>
			<DashboardLayout>
				<DashboardNavbar isMini />
				<MDBox mt={4}>
					<Grid container spacing={3}>
						<Grid item xs={12} lg={9}>
							{isNew || (data && !isLoading && !error) ? (
								<PrintableBox mb={3} ref={contentRef}>
									<MainContent />
								</PrintableBox>
							) : (
								<LoadingComponent type="box" />
							)}
						</Grid>
						<Grid item xs={12} lg={3}>
							{isNew || (data && !isLoading && !error) ? (
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<ManageCard
											save={{
												disabled: user.data.role === 'VIEWER',
												onClicked: async () => {
													if (isNew) {
														const newBusinessAccount = await saveNewBusinessAccount()
														const newBusinessAccountRoute: RouteType[] = [
															{
																type: 'invisible',
																name: `Business Account: ${newBusinessAccount.id}`,
																key: `business-account-${newBusinessAccount.id}`,
																route: `${ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE}/${newBusinessAccount.id}`,
																component: <BusinessAccount />,
																authRequired: true,
															},
														]
														updateRoutes(dispatch, routes, newBusinessAccountRoute)
														navigate(
															`${ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE}/${newBusinessAccount.id}`
														)
													} else {
														await updateBusinessAccount()
														refetch()
													}
												},
											}}
											print={{ onClicked: reactToPrintFn }}
											deleteItem={{
												visible: !isNew,
												onClicked: async () => {
													await deleteBusinessAccount()
													queryClient.invalidateQueries(['business-account-table'])
													navigate(ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE)
													updateRoutes(
														dispatch,
														routes,
														routes.filter(r => r.key !== `business-account-${accountId}`)
													)
												},
											}}
											formEdited={formEdited}
											setFormEdited={setFormEdited}
										/>
									</Grid>
									{!isNew && (
										<Grid item xs={12}>
											<ChangeLog log={data?.changeLog} />
										</Grid>
									)}
								</Grid>
							) : (
								<LoadingComponent type="box" height={'30rem'} />
							)}
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		</FormContext.Provider>
	)
}

export default BusinessAccount
